/* eslint-disable max-statements */
import axios from 'axios';
import { Kitchen } from '@rta/rta-blueprint-library';

import { CustomerState } from '@state';

type TaxPayloadItem = {
	qty: number;
	amount: number;
	sku: string;
}

type TaxPayload = {
	zipcode: string;
	address?: string;
	city?: string;
	state?: string;
	country?: string;
	customerCode: string;
	items: Array<TaxPayloadItem>;
}

export type TaxBreakdownItem = {
	taxName: string;
	tax: number;
}

export type TaxResponse = {
	totalTax: number;
	rate: number;
	taxBreakdown: Array<TaxBreakdownItem>;
}

export type FetchTaxParams = {
	kitchen: Kitchen,
	customer: CustomerState
	getToken: () => Promise<string>,
	setTax?: (tax: number) => void,
	setError?: (error: string) => void
};

export const fetchTax = async (params: FetchTaxParams): Promise<[TaxResponse | null, string | null]> => {
	const { kitchen, customer, getToken, setTax, setError } = params;
	if (!customer.zipcode) {
		console.warn('fetchTax: no zipcode provided — skipping tax calculation');
		return [null, 'Missing zipcode for tax calculation'];
	}

	if (kitchen) {
		const { itemizedMsrp } = kitchen;
		const taxPayload: TaxPayload = {
			zipcode: customer.zipcode,
			address: customer.address,
			city: customer.city,
			state: customer.state,
			country: customer.country,
			customerCode: customer.email,
			items: itemizedMsrp.items.map((item) => ({
				qty: item.qty,
				amount: item.each,
				sku: item.sku
			}))
		};

		// add countertops
		itemizedMsrp.countertops.forEach((countertop) => {
			if (countertop.total === 0) return;
			taxPayload.items.push({
				qty: countertop.qty,
				amount: countertop.each,
				sku: 'countertop'
			});
		});

		// add islands
		itemizedMsrp.islands.forEach((island) => {
			if (island.total === 0) return;
			taxPayload.items.push({
				qty: island.qty,
				amount: island.each,
				sku: island.sku
			});
		});

		// add accessories
		itemizedMsrp.accessories.forEach((accessory) => {
			if (accessory.total === 0) return;
			taxPayload.items.push({
				qty: accessory.qty,
				amount: accessory.each,
				sku: accessory.sku
			});
		});

		// add backsplashes
		itemizedMsrp.backsplashes.forEach((backsplash) => {
			if (backsplash.total === 0) return;
			taxPayload.items.push({
				qty: backsplash.qty,
				amount: backsplash.each,
				sku: backsplash.sku
			});
		});

		// add shipping as a taxable line item
		if (!itemizedMsrp.isFreeShipping && itemizedMsrp.shippingAmount > 0) {
			taxPayload.items.push({
				qty: 1,
				amount: itemizedMsrp.shippingAmount,
				sku: 'SHIPPING'
			});
		}

		const lineItemTotal = taxPayload.items.reduce((sum, item) => sum + item.amount, 0);
		const promotional = itemizedMsrp.subtotal - lineItemTotal;
		if (promotional !== 0) {
			taxPayload.items.push({
				qty: 1,
				amount: promotional,
				sku: 'promotion'
			});
		}

		// no need to call the api if there are no items
		if (taxPayload.items.length === 0) {
			if (setTax) setTax(0);
			return [{ totalTax: 0, rate: 0, taxBreakdown: [] }, null];
		}

		try {
			const token = await getToken();
			const response = await axios.post(`${import.meta.env.VITE_API_URL}/calculateSalesTax`, taxPayload, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			});

			if (response.status === 200) {
				const taxResponse: TaxResponse = {
					totalTax: response.data.totalTax,
					rate: response.data.rate,
					taxBreakdown: response.data.taxBreakdown
				};
				if (setTax) setTax(taxResponse.totalTax);
				return [taxResponse, null];
			}
		} catch (e) {
			console.warn('error fetching tax', e);
		}

		if (setError) setError('Error fetching tax');
		return [null, 'Error fetching tax'];
	}

	console.warn('fetchTax: no kitchen data — skipping tax calculation');
	return [null, null];
};
